import { useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import type { AddonItem } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { isEqualCurrency } from 'hb-react/shared/apps/Events/utils/isEqualCurrency';
import {
  Checkbox,
  CustomAttribute,
} from 'hb-react/shared/components/formik/components/inputs';
import { Price } from 'hb-react/shared/components/Price';
import { useAmplitude } from 'hb-react/shared/hooks/useAmplitude';

import { Image } from './Image';

const AddonCheckbox = ({ addon, name }: { name: string; addon: AddonItem }) => {
  const { setFieldValue } = useFormikContext();
  const { trackEvent } = useAmplitude();
  const { cart } = useBooking();
  const selectedCurrency = cart?.currency;

  const [selected, setSelected] = useState(
    addon?.cartItem?.quantity && addon.cartItem.quantity > 0,
  );

  const isDisabled = useMemo(
    () => !isEqualCurrency(addon.currency, selectedCurrency) || !addon.buyable,
    [addon, selectedCurrency],
  );

  const label = useMemo(
    () => (
      <>
        {addon.name} - (<Price price={addon.price} currency={addon.currency} />)
      </>
    ),
    [addon],
  );

  const displayCustomAttributes = useMemo(
    () => selected && addon.customizable_attributes?.length > 0,
    [selected, addon.customizable_attributes],
  );

  const toggle = useCallback(
    (checked) => {
      if (isDisabled) {
        return false;
      }

      setSelected(checked);

      setFieldValue(
        name,
        checked
          ? {
              ...addon,
              cartItem: {
                ...addon.cartItem,
                quantity: 1,
              },
              selected: true,
            }
          : {
              ...addon,
              cartItem: {
                ...addon.cartItem,
                quantity: 0,
              },
              selected: false,
            },
      );

      trackEvent(
        checked
          ? 'NetworkEvents|User selects an addon'
          : 'NetworkEvents|User unselects an addon',
        {
          price: addon.price,
        },
      );
    },
    [isDisabled, addon, name, setFieldValue, trackEvent],
  );

  return (
    <CheckboxWrapper $isDisabled={isDisabled} data-testid="addon-checkbox">
      <Box mr={2} alignItems="flex-start">
        <Checkbox
          id={`${name}[selected]-id`}
          name={`${name}[selected]`}
          disabled={isDisabled}
          onChange={toggle}
          wrapperProps={{ mb: 0 }}
        />
      </Box>
      <Box flexGrow={1} flexDirection="column">
        <Box
          flexGrow={1}
          flexDirection={{ _: 'column', m: 'row' }}
          justifyContent="space-between"
          mb={displayCustomAttributes ? 3 : 0}
          alignItems="flex-start"
        >
          <Box
            as="label"
            htmlFor={`${name}[selected]-id`}
            flexGrow={1}
            flexDirection="column"
          >
            <LabelWrapper display="block" fontWeight={1}>
              {label}
            </LabelWrapper>
            {addon.description && (
              <Box font="body-dove-italic" mt={1}>
                {addon.description}
              </Box>
            )}
          </Box>

          {addon.image && (
            <Box ml={{ _: 0, m: 4 }} mr={4} mt={{ _: 3, m: 0 }}>
              <Image
                src={addon.image}
                thumb={addon.thumb}
                width={92}
                height={92}
              />
            </Box>
          )}
        </Box>

        {displayCustomAttributes && (
          <Box maxWidth={508}>
            <Box display="block" width="100%">
              {addon.customizable_attributes.map((attribute) => (
                <CustomAttribute
                  key={`${name}[${attribute.name}]`}
                  name={`${name}[${attribute.name}]`}
                  customAttribute={attribute}
                  // @ts-expect-error: fix types
                  labelMultiline
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </CheckboxWrapper>
  );
};

export default AddonCheckbox;

const CheckboxWrapper = styled(Box)<{ $isDisabled: boolean }>`
  opacity: ${({ $isDisabled }) => ($isDisabled ? '0.6' : '1')};
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'auto' : 'pointer')};
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'all')};
`;

const LabelWrapper = styled(Box)`
  padding-top: 1px;
`;
